import { useWallet } from "@solana/wallet-adapter-react";
import {
  PublicKey,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import { useSecureRPC } from '../hooks/useSecureRPC';
import {
  getAssociatedTokenAddress,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  createAssociatedTokenAccountIdempotentInstruction,
  createTransferCheckedInstruction,
  createBurnCheckedInstruction,
  createMintToInstruction,
  createThawAccountInstruction,
  createFreezeAccountInstruction,
  AccountState,
} from "@solana/spl-token";
import {
  createPauseInstruction,
  createResumeInstruction,
  parsePausableConfigFromMint,
  PausableConfig,
} from '../utils/pausableInstructions';
import {
  parseConfidentialTransferMintFromMint,
  ConfidentialTransferMintConfig,
} from '../utils/confidentialTransferInstructions';
import {
  parsePermanentDelegateFromMint,
  PermanentDelegateConfig,
} from '../utils/permanentDelegateInstructions';
import {
  parseDefaultAccountStateFromMint,
  DefaultAccountStateConfig,
} from '../utils/defaultAccountStateInstructions';
import { FC, useState, useCallback, useMemo, useEffect } from "react";
import { notify } from "utils/notifications";
import { ClipLoader } from "react-spinners";
import Link from "next/link";
import styles from "./ManageToken.module.css";
import {
  getTokenAuthoritiesSecure,
  resolveTokenMetadataSecure,
  TokenMetadataSource,
  createRevokeMintAuthorityInstruction,
  createRevokeFreezeAuthorityInstruction,
  createRevokeUpdateAuthorityInstructionSecure,
  createRevokeTokenMetadataUpdateAuthorityInstruction,
  createMintAdditionalTokensInstruction,
} from '../utils/token';
import { FEATURES } from '../config';

interface TokenState {
  mintAuthority: PublicKey | null;
  freezeAuthority: PublicKey | null;
  supply: bigint;
  decimals: number;
  metadataExists: boolean;
  isMetadataMutable: boolean;
  metadataUpdateAuthority: PublicKey | null;
  metadataSource: TokenMetadataSource;
  programId: PublicKey;
  pausable: PausableConfig | null;
  confidentialTransfer: ConfidentialTransferMintConfig | null;
  permanentDelegate: PermanentDelegateConfig | null;
  defaultAccountState: DefaultAccountStateConfig | null;
}

const shortAddr = (addr: string, left = 4, right = 4) =>
  addr.length > left + right + 3 ? `${addr.slice(0, left)}…${addr.slice(-right)}` : addr;

const MAX_BATCH_RECIPIENTS = 200;
// Stay comfortably under the 1232-byte packet limit when packing instructions.
const MAX_TX_BYTES = 1180;

interface BatchRow {
  address: PublicKey;
  amountRaw: string;
  line: number;
}
interface BatchParseResult {
  rows: BatchRow[];
  errors: string[];
}

// Parses a free-form `address, amount` list (one recipient per line). Accepts
// comma / semicolon / whitespace separators, skips blank lines and an optional
// `address,amount` header row. Returns valid rows plus human-readable errors.
const parseBatchRecipients = (text: string): BatchParseResult => {
  const rows: BatchRow[] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    if (!line) { return; }

    const parts = line.split(/[\s,;]+/).filter(Boolean);
    if (parts.length < 2) {
      errors.push(`Line ${idx + 1}: expected "address, amount"`);
      return;
    }

    const [addrStr, amountStr] = parts;
    // Tolerate a header row like "address,amount".
    if (/^address$/i.test(addrStr) && /^amount$/i.test(amountStr)) { return; }

    let address: PublicKey;
    try {
      address = new PublicKey(addrStr);
    } catch {
      errors.push(`Line ${idx + 1}: invalid address "${shortAddr(addrStr, 6, 4)}"`);
      return;
    }

    const amountNum = parseFloat(amountStr);
    if (!isFinite(amountNum) || amountNum <= 0) {
      errors.push(`Line ${idx + 1}: invalid amount "${amountStr}"`);
      return;
    }

    rows.push({ address, amountRaw: amountStr, line: idx + 1 });
  });

  if (rows.length > MAX_BATCH_RECIPIENTS) {
    errors.push(`Too many recipients (${rows.length}). Max ${MAX_BATCH_RECIPIENTS} per batch.`);
  }

  return { rows, errors };
};

interface AddressBatchParseResult {
  rows: PublicKey[];
  errors: string[];
}

// Parses a list of wallet addresses (one per line). Accepts commas/whitespace as
// separators within a line so a CSV of "address" with stray trailing commas is
// tolerated. Skips blank lines, deduplicates, and surfaces parse errors.
const parseBatchAddresses = (text: string): AddressBatchParseResult => {
  const rows: PublicKey[] = [];
  const errors: string[] = [];
  const seen = new Set<string>();
  const lines = text.split(/\r?\n/);

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    if (!line) { return; }
    const parts = line.split(/[\s,;]+/).filter(Boolean);
    if (parts.length === 0) { return; }
    const addrStr = parts[0];
    if (/^address$/i.test(addrStr)) { return; }

    let address: PublicKey;
    try {
      address = new PublicKey(addrStr);
    } catch {
      errors.push(`Line ${idx + 1}: invalid address "${shortAddr(addrStr, 6, 4)}"`);
      return;
    }
    const key = address.toString();
    if (seen.has(key)) { return; }
    seen.add(key);
    rows.push(address);
  });

  if (rows.length > MAX_BATCH_RECIPIENTS) {
    errors.push(`Too many addresses (${rows.length}). Max ${MAX_BATCH_RECIPIENTS} per batch.`);
  }

  return { rows, errors };
};

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * The two flows the page head marks up as HowTo, rendered visibly below the
 * primer. Same contract as MANAGE_TOKEN_FAQS: one array feeds both the UI and
 * the structured data, because a HowTo describing steps a reader cannot see on
 * the page violates Google's structured-data guidelines.
 */
export const MANAGE_TOKEN_HOWTOS: {
  id: string;
  name: string;
  description: string;
  steps: { name: string; text: string }[];
}[] = [
  {
    id: 'revoke-mint',
    name: 'How to revoke mint authority on Solana',
    description:
      "Permanently lock an SPL or Token-2022 token's supply by revoking its mint authority. The action is irreversible.",
    steps: [
      {
        name: 'Load the token',
        text: 'Paste the mint address into the search box above and load it. The tool reads the mint account from the chain and shows who currently holds the mint, freeze, and update authorities. No signature is required to look.',
      },
      {
        name: 'Connect the wallet holding the authority',
        text: 'Connect the Solana wallet that currently holds the mint authority. Only that wallet can sign the revocation.',
      },
      {
        name: 'Revoke the mint authority',
        text: 'Use the Revoke control on the mint authority card to build the SetAuthority transaction that sets the mint authority to none.',
      },
      {
        name: 'Confirm the transaction',
        text: 'Approve the transaction in your wallet. Once it confirms, the supply is permanently fixed and nobody — including you — can mint more.',
      },
    ],
  },
  {
    id: 'make-immutable',
    name: "How to make a Solana token's metadata immutable",
    description:
      "Permanently lock a token's name, symbol, and URI by revoking its metadata update authority.",
    steps: [
      {
        name: 'Load the token',
        text: 'Enter the token mint address above and load it to see the current update authority alongside the mint and freeze authorities.',
      },
      {
        name: 'Connect the wallet holding update authority',
        text: 'Connect the wallet that holds the metadata update authority for the token.',
      },
      {
        name: 'Revoke the update authority',
        text: "Use the Revoke control on the update authority card to permanently lock the token's metadata.",
      },
      {
        name: 'Confirm the transaction',
        text: 'Approve the transaction in your wallet. The name, symbol, and URI can never be changed again.',
      },
    ],
  },
];

/**
 * The questions this page renders, hoisted to module scope so
 * src/pages/manage-token.tsx can build its FAQPage markup from the very same
 * array. The page head used to hardcode five questions of its own, two of
 * which answered differently from the text below -- schema that disagrees
 * with the page is worse than no schema.
 *
 * `answerText` exists only on the entry whose answer contains a link; it is
 * the plain-text rendering used by the markup.
 */
type ManageTokenFaq = {
  id: string;
  question: string;
  answer: React.ReactNode;
  answerText?: string;
};

export const MANAGE_TOKEN_FAQS: ManageTokenFaq[] = [
  {
    id: 'renounce-ownership',
    question: 'How do I renounce ownership on Solana?',
    answer: 'On Solana, "renouncing ownership" is done by revoking the mint authority. This permanently prevents anyone from minting additional tokens, effectively locking the total supply. Use the "Revoke Mint Authority" feature above.'
  },
  {
    id: 'undo-revoke-mint',
    question: 'Can I undo revoking mint authority?',
    answer: 'No, revoking mint authority is permanent and irreversible. Once revoked, no one can mint additional tokens, including the original mint authority holder. This is by design to ensure token supply integrity.'
  },
  {
    id: 'mint-vs-freeze',
    question: 'What\'s the difference between revoking mint authority and freeze authority?',
    answer: 'Mint authority controls creating new tokens (supply). Freeze authority controls freezing token accounts (preventing transfers). You can revoke either or both independently. Revoking mint authority locks supply, while revoking freeze authority ensures tokens can always be transferred.'
  },
  {
    id: 'stop-minting',
    question: 'How do I stop minting more tokens on Solana?',
    answer: 'Revoke the mint authority using the tool above. This permanently locks the token supply and prevents any additional tokens from being created. This is the Solana equivalent of "burning" the mint function.'
  },
  {
    id: 'mint-after-revoke',
    question: 'Can I mint tokens after revoking mint authority?',
    answer: 'No, once mint authority is revoked, no one can mint additional tokens. The total supply becomes permanently fixed at the current amount. This action cannot be reversed.'
  },
  {
    id: 'immutable-metadata',
    question: 'How do I make my Solana token metadata immutable?',
    answer: 'Use the "Revoke Update Authority" feature above. This permanently removes the ability to modify your token\'s metadata (name, symbol, URI), making it effectively immutable. This ensures your token information cannot be changed after revocation.'
  },
  {
    id: 'revoke-freeze-benefits',
    question: 'Why should I revoke freeze authority?',
    answer: 'Revoking freeze authority ensures that token holders can always transfer their tokens, increasing trust and decentralization. It prevents anyone from freezing accounts, which could lock holders out of their tokens. This is especially important for community tokens and decentralized projects.'
  },
  {
    id: 'gas-fees',
    question: 'How much does it cost to revoke authorities?',
    answer: 'Revoking authorities only requires standard Solana network transaction fees (typically less than $0.01). Our tool is completely free to use - you only pay the minimal network fees required by the Solana blockchain.'
  },
  {
    id: 'wallet-requirements',
    question: 'What wallet do I need to manage my token?',
    answer: 'The wallet that holds the authority you want to revoke. This is typically the wallet you used to create the token.'
  },
  {
    id: 'check-authorities',
    question: 'How can I check who has authority over my token?',
    answer: 'Use the tool above by entering your token mint address and clicking "Load Token". The tool will display all current authorities (mint, freeze, and update) so you can see who has control over different aspects of your token.'
  },
  {
    id: 'mint-additional-tokens',
    question: 'Can I mint additional tokens to specific addresses?',
    answer: 'Yes. In the "Mint additional tokens" section, switch to "Single wallet" to mint to any address (leave it blank to mint to your connected wallet), or "Multiple wallets" to mint to many addresses at once — paste an address, amount list or upload a CSV. Missing token accounts are created automatically. This requires that you still hold the mint authority.'
  },
  {
    id: 'security-benefits',
    question: 'What are the security benefits of revoking authorities?',
    answer: 'Revoking authorities increases trust and security by: 1) Locking supply prevents inflation, 2) Removing freeze authority ensures holders can always transfer, 3) Revoking update authority prevents metadata changes. This makes your token more trustworthy and decentralized.'
  },
  {
    id: 'token-creation',
    question: 'Can I create a token with authorities already revoked?',
    answer: (
      <>
        Yes! You can create a token with authorities already revoked using our{' '}
        <Link href="/create">token creation tool</Link>
        . For a flat fee of 0.05 SOL, you can create your token with optional authority revocation, add social links, and even set custom mint addresses. This lets you start with the standard on-chain trust signals in place.
      </>
    ) as React.ReactNode,
    answerText:
      'Yes. You can create a token with authorities already revoked using our token creation tool at soulminter.io/create. For a flat fee of 0.05 SOL, you can create your token with optional authority revocation, add social links, and even set custom mint addresses. This lets you start with the standard on-chain trust signals in place.',
  },
  {
    id: 'multiple-authorities',
    question: 'Can multiple wallets have authority over a token?',
    answer: 'No, each authority (mint, freeze, update) can only be held by one wallet at a time. However, different wallets can hold different authorities. For example, one wallet might have mint authority while another has freeze authority.'
  },
  {
    id: 'transaction-time',
    question: 'How long does it take to revoke an authority?',
    answer: 'Revoking an authority is a standard Solana transaction that typically confirms within seconds. The transaction is usually finalized within 1-2 block confirmations, which takes about 1-2 seconds on the Solana network.'
  }
];

/** Plain-text answers for the FAQPage node in the page head. */
export const MANAGE_TOKEN_FAQ_ITEMS = MANAGE_TOKEN_FAQS.map((faq) => ({
  q: faq.question,
  a: faq.answerText ?? (typeof faq.answer === "string" ? faq.answer : ""),
})).filter((faq) => faq.a.length > 0);

export const ManageToken: FC = () => {
  const { publicKey, signTransaction, signAllTransactions, signMessage } = useWallet();
  const secureRPC = useSecureRPC();

  const [mintAddressInput, setMintAddressInput] = useState("");
  const [mintAmount, setMintAmount] = useState("");
  const [mintMode, setMintMode] = useState<'single' | 'batch'>('single');
  const [mintRecipient, setMintRecipient] = useState("");
  const [batchInput, setBatchInput] = useState("");
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [pdSourceOwner, setPdSourceOwner] = useState("");
  const [pdDestOwner, setPdDestOwner] = useState("");
  const [pdTransferAmount, setPdTransferAmount] = useState("");
  const [pdBurnSourceOwner, setPdBurnSourceOwner] = useState("");
  const [pdBurnAmount, setPdBurnAmount] = useState("");
  const [whitelistMode, setWhitelistMode] = useState<'thaw' | 'freeze'>('thaw');
  const [whitelistInput, setWhitelistInput] = useState("");
  const [whitelistProgress, setWhitelistProgress] = useState<{ current: number; total: number } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [tokenState, setTokenState] = useState<TokenState | null>(null);
  const [currentMintAddress, setCurrentMintAddress] = useState<PublicKey | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Auditor decrypt state — kept in raw form (bigint sk) so we never persist it
  // anywhere; lives only in the React state of this component instance.
  const [auditorSecret, setAuditorSecret] = useState<bigint | null>(null);
  const [auditorPubkeyBytes, setAuditorPubkeyBytes] = useState<Uint8Array | null>(null);
  const [auditorTxSig, setAuditorTxSig] = useState("");
  const [auditorBusy, setAuditorBusy] = useState(false);
  const [auditorResult, setAuditorResult] = useState<{ amount: bigint; ok: true } | { error: string; ok: false } | null>(null);

  const copyToClipboard = useCallback(async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1400);
    } catch {
      notify({ type: 'error', message: 'Could not copy to clipboard' });
    }
  }, []);

  const fetchTokenInfo = useCallback(async () => {
    if (!mintAddressInput.trim()) {
      notify({ type: 'error', message: 'Please enter a valid mint address' });
      return;
    }

    let mintAddress: PublicKey;
    try {
      mintAddress = new PublicKey(mintAddressInput.trim());
    } catch {
      notify({ type: 'error', message: 'Invalid mint address format' });
      return;
    }

    setIsFetching(true);
    try {
      const authorities = await getTokenAuthoritiesSecure(
        secureRPC.getAccountInfo,
        mintAddress
      );

      const metadataInfo = await resolveTokenMetadataSecure(
        secureRPC.getAccountInfo,
        mintAddress,
        authorities.rawData,
        authorities.programId,
      );

      const pausable = authorities.programId.equals(TOKEN_2022_PROGRAM_ID)
        ? parsePausableConfigFromMint(authorities.rawData)
        : null;
      const confidentialTransfer = authorities.programId.equals(TOKEN_2022_PROGRAM_ID)
        ? parseConfidentialTransferMintFromMint(authorities.rawData)
        : null;
      const permanentDelegate = authorities.programId.equals(TOKEN_2022_PROGRAM_ID)
        ? parsePermanentDelegateFromMint(authorities.rawData)
        : null;
      const defaultAccountState = authorities.programId.equals(TOKEN_2022_PROGRAM_ID)
        ? parseDefaultAccountStateFromMint(authorities.rawData)
        : null;

      setTokenState({
        mintAuthority: authorities.mintAuthority,
        freezeAuthority: authorities.freezeAuthority,
        supply: authorities.supply,
        decimals: authorities.decimals,
        metadataExists: metadataInfo.exists,
        isMetadataMutable: metadataInfo.isMutable,
        metadataUpdateAuthority: metadataInfo.updateAuthority,
        metadataSource: metadataInfo.source,
        programId: authorities.programId,
        pausable,
        confidentialTransfer,
        permanentDelegate,
        defaultAccountState,
      });
      setCurrentMintAddress(mintAddress);

      notify({ type: 'success', message: 'Token information loaded successfully' });
    } catch (error: any) {
      console.error('Error fetching token info:', error);
      notify({
        type: 'error',
        message: error.message || 'Failed to fetch token information. Make sure the token exists.'
      });
      setTokenState(null);
      setCurrentMintAddress(null);
    } finally {
      setIsFetching(false);
    }
  }, [secureRPC, mintAddressInput]);

  const sendWithFallback = useCallback(async (rawTxBase64: string) => {
    try {
      return await secureRPC.sendTransaction(rawTxBase64, {
        encoding: 'base64',
        skipPreflight: false,
        preflightCommitment: 'processed',
        maxRetries: 3,
      });
    } catch (err: any) {
      const msg: string = err?.message || '';
      if (
        msg.includes('simulation failed') ||
        msg.includes('already been processed') ||
        msg.includes('Transaction was previously processed') ||
        msg.includes('Blockhash not found') ||
        msg.includes('Transaction expired')
      ) {
        console.warn('sendWithFallback: simulation failed, retrying without preflight');
        return await secureRPC.sendTransaction(rawTxBase64, {
          encoding: 'base64',
          skipPreflight: true,
          preflightCommitment: 'processed',
          maxRetries: 5,
        });
      }
      throw err;
    }
  }, [secureRPC]);

  const confirmSignature = useCallback(async (signature: string) => {
    const confirmationTimeout = 60000;
    const startTime = Date.now();

    while (Date.now() - startTime < confirmationTimeout) {
      let status: any = null;
      try {
        const statusResp = await secureRPC.getSignatureStatuses([signature]);
        status = statusResp?.value?.[0];
      } catch {
        // Network hiccup — keep polling.
      }

      if (status) {
        if (status.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
        }
        if (status.confirmationStatus === 'finalized' || status.confirmationStatus === 'confirmed') {
          return signature;
        }
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    return signature;
  }, [secureRPC]);

  const sendSignedTransaction = useCallback(async (signedTx: Transaction) => {
    const rawTx = signedTx.serialize().toString('base64');
    const signature = await sendWithFallback(rawTx);
    return confirmSignature(signature);
  }, [sendWithFallback, confirmSignature]);

  const sendTransaction = useCallback(async (transaction: Transaction) => {
    if (!publicKey || !signTransaction) {
      throw new Error('Wallet not connected');
    }

    const { blockhash, lastValidBlockHeight } = await secureRPC.getLatestBlockhash('processed');
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;
    transaction.feePayer = publicKey;

    const signedTx = await signTransaction(transaction);
    return sendSignedTransaction(signedTx);
  }, [publicKey, signTransaction, secureRPC, sendSignedTransaction]);

  const handleRevokeMintAuthority = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }

    if (!tokenState.mintAuthority) {
      notify({ type: 'error', message: 'Mint authority is already revoked' });
      return;
    }

    if (!tokenState.mintAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the mint authority for this token' });
      return;
    }

    setIsLoading(true);
    try {
      const transaction = new Transaction();
      const instruction = createRevokeMintAuthorityInstruction(
        currentMintAddress,
        publicKey,
        tokenState.programId
      );
      transaction.add(instruction);

      const signature = await sendTransaction(transaction);

      notify({
        type: 'success',
        message: 'Mint authority revoked successfully',
        txid: signature,
      });

      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error revoking mint authority:', error);
      notify({
        type: 'error',
        message: error.message || 'Failed to revoke mint authority'
      });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, sendTransaction, fetchTokenInfo]);

  const handleRevokeFreezeAuthority = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }

    if (!tokenState.freezeAuthority) {
      notify({ type: 'error', message: 'Freeze authority is already revoked' });
      return;
    }

    if (!tokenState.freezeAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the freeze authority for this token' });
      return;
    }

    setIsLoading(true);
    try {
      const transaction = new Transaction();
      const instruction = createRevokeFreezeAuthorityInstruction(
        currentMintAddress,
        publicKey,
        tokenState.programId
      );
      transaction.add(instruction);

      const signature = await sendTransaction(transaction);

      notify({
        type: 'success',
        message: 'Freeze authority revoked successfully',
        txid: signature,
      });

      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error revoking freeze authority:', error);
      notify({
        type: 'error',
        message: error.message || 'Failed to revoke freeze authority'
      });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, sendTransaction, fetchTokenInfo]);

  const handleRevokeUpdateAuthority = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }

    if (!tokenState.metadataExists) {
      notify({ type: 'error', message: 'Token metadata does not exist' });
      return;
    }

    if (!tokenState.metadataUpdateAuthority) {
      notify({ type: 'error', message: 'Update authority is already revoked' });
      return;
    }

    if (!tokenState.metadataUpdateAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the update authority for this token' });
      return;
    }

    setIsLoading(true);
    try {
      const transaction = new Transaction();
      const instruction = tokenState.metadataSource === 'token-2022-extension'
        ? createRevokeTokenMetadataUpdateAuthorityInstruction(currentMintAddress, publicKey)
        : await createRevokeUpdateAuthorityInstructionSecure(
          secureRPC.getAccountInfo,
          currentMintAddress,
          publicKey
        );
      transaction.add(instruction);

      const signature = await sendTransaction(transaction);

      notify({
        type: 'success',
        message: 'Update authority revoked successfully',
        txid: signature,
      });

      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error revoking update authority:', error);
      notify({
        type: 'error',
        message: error.message || 'Failed to revoke update authority. You may not be the update authority.'
      });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, secureRPC, sendTransaction, fetchTokenInfo]);

  const handleMintAdditionalTokens = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }

    if (!tokenState.mintAuthority) {
      notify({ type: 'error', message: 'Mint authority is revoked. Cannot mint additional tokens.' });
      return;
    }

    if (!tokenState.mintAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the mint authority for this token' });
      return;
    }

    if (!mintAmount || parseFloat(mintAmount) <= 0) {
      notify({ type: 'error', message: 'Please enter a valid mint amount' });
      return;
    }

    // Blank recipient = mint to the connected wallet (original behavior).
    let recipient: PublicKey = publicKey;
    const recipientInput = mintRecipient.trim();
    if (recipientInput) {
      try {
        recipient = new PublicKey(recipientInput);
      } catch {
        notify({ type: 'error', message: 'Invalid recipient wallet address' });
        return;
      }
    }

    setIsLoading(true);
    try {
      const amount = BigInt(Math.floor(parseFloat(mintAmount) * Math.pow(10, tokenState.decimals)));
      const programId = tokenState.programId;

      const transaction = new Transaction();

      const associatedTokenAccount = await getAssociatedTokenAddress(
        currentMintAddress,
        recipient,
        false,
        programId,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const ataInfoResponse = await secureRPC.getAccountInfo(associatedTokenAccount.toString());
      const ataInfo = ataInfoResponse?.value;
      if (!ataInfo) {
        transaction.add(
          createAssociatedTokenAccountInstruction(
            publicKey,
            associatedTokenAccount,
            recipient,
            currentMintAddress,
            programId,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      const { instruction } = await createMintAdditionalTokensInstruction(
        currentMintAddress,
        recipient,
        amount,
        publicKey,
        programId
      );
      transaction.add(instruction);

      const signature = await sendTransaction(transaction);

      notify({
        type: 'success',
        message: recipientInput
          ? `Minted ${mintAmount} tokens to ${shortAddr(recipient.toString(), 6, 6)}`
          : `Successfully minted ${mintAmount} tokens`,
        txid: signature,
      });

      await fetchTokenInfo();
      setMintAmount("");
      setMintRecipient("");
    } catch (error: any) {
      console.error('Error minting additional tokens:', error);
      notify({
        type: 'error',
        message: error.message || 'Failed to mint additional tokens'
      });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, mintAmount, mintRecipient, secureRPC, sendTransaction, fetchTokenInfo]);

  const handleBatchMint = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    if (!tokenState.mintAuthority) {
      notify({ type: 'error', message: 'Mint authority is revoked. Cannot mint additional tokens.' });
      return;
    }
    if (!tokenState.mintAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the mint authority for this token' });
      return;
    }

    const { rows, errors } = parseBatchRecipients(batchInput);
    if (errors.length > 0) {
      notify({ type: 'error', message: `Fix ${errors.length} invalid row${errors.length === 1 ? '' : 's'} before minting` });
      return;
    }
    if (rows.length === 0) {
      notify({ type: 'error', message: 'Add at least one recipient (address, amount per line)' });
      return;
    }

    if (!signAllTransactions && !signTransaction) {
      notify({ type: 'error', message: 'This wallet cannot sign transactions' });
      return;
    }

    setIsLoading(true);
    setBatchProgress(null);
    try {
      const programId = tokenState.programId;

      // Resolve every recipient's ATA, then batch-check which already exist.
      const atas = await Promise.all(
        rows.map((r) =>
          getAssociatedTokenAddress(currentMintAddress, r.address, false, programId, ASSOCIATED_TOKEN_PROGRAM_ID)
        )
      );

      const existing = new Set<string>();
      for (let i = 0; i < atas.length; i += 100) {
        const chunk = atas.slice(i, i + 100);
        const resp = await secureRPC.callRPC('getMultipleAccounts', [
          chunk.map((a) => a.toString()),
          { encoding: 'base64', commitment: 'confirmed' },
        ]);
        const values: any[] = resp?.value || [];
        values.forEach((v, j) => {
          if (v) { existing.add(chunk[j].toString()); }
        });
      }

      // Build the instruction group for each recipient. createATA is emitted at
      // most once per unique ATA across the whole batch (handles duplicates).
      const createdAtas = new Set<string>();
      const groups: TransactionInstruction[][] = rows.map((r, i) => {
        const ata = atas[i];
        const ataStr = ata.toString();
        const ixs: TransactionInstruction[] = [];
        if (!existing.has(ataStr) && !createdAtas.has(ataStr)) {
          ixs.push(
            createAssociatedTokenAccountInstruction(
              publicKey, ata, r.address, currentMintAddress, programId, ASSOCIATED_TOKEN_PROGRAM_ID
            )
          );
          createdAtas.add(ataStr);
        }
        const amount = BigInt(Math.floor(parseFloat(r.amountRaw) * Math.pow(10, tokenState.decimals)));
        ixs.push(createMintToInstruction(currentMintAddress, ata, publicKey, amount, [], programId));
        return ixs;
      });

      // One blockhash for the whole batch so every tx can be signed in a single
      // wallet prompt. Pack recipient groups into as few transactions as the
      // packet-size limit allows.
      const { blockhash, lastValidBlockHeight } = await secureRPC.getLatestBlockhash('finalized');
      const freshTx = () => {
        const tx = new Transaction();
        tx.recentBlockhash = blockhash;
        tx.lastValidBlockHeight = lastValidBlockHeight;
        tx.feePayer = publicKey;
        return tx;
      };

      const txs: Transaction[] = [];
      let current = freshTx();
      for (const group of groups) {
        const trial = freshTx();
        trial.add(...current.instructions, ...group);
        const size = trial.serialize({ requireAllSignatures: false, verifySignatures: false }).length;
        if (current.instructions.length > 0 && size > MAX_TX_BYTES) {
          txs.push(current);
          current = freshTx();
        }
        current.add(...group);
      }
      if (current.instructions.length > 0) { txs.push(current); }

      // Sign everything up front (one prompt where supported).
      let signed: Transaction[];
      if (signAllTransactions) {
        signed = await signAllTransactions(txs);
      } else if (signTransaction) {
        signed = [];
        for (const tx of txs) {
          // eslint-disable-next-line no-await-in-loop
          signed.push(await signTransaction(tx));
        }
      } else {
        throw new Error('This wallet cannot sign transactions');
      }

      const sigs: string[] = [];
      const failures: { index: number; error: string }[] = [];
      for (let i = 0; i < signed.length; i++) {
        setBatchProgress({ current: i + 1, total: signed.length });
        try {
          // eslint-disable-next-line no-await-in-loop
          const sig = await sendSignedTransaction(signed[i]);
          sigs.push(sig);
        } catch (e: any) {
          failures.push({ index: i + 1, error: e?.message || 'unknown error' });
        }
      }

      if (failures.length === 0) {
        notify({
          type: 'success',
          message: `Minted to ${rows.length} wallet${rows.length === 1 ? '' : 's'} across ${sigs.length} transaction${sigs.length === 1 ? '' : 's'}`,
          txid: sigs[sigs.length - 1],
        });
        setBatchInput("");
      } else if (sigs.length > 0) {
        console.error('Batch mint — failed transactions:', failures);
        notify({
          type: 'info',
          message: `${sigs.length}/${signed.length} transactions confirmed. ${failures.length} failed — see console for details.`,
        });
      } else {
        console.error('Batch mint — failed transactions:', failures);
        notify({ type: 'error', message: 'Batch mint failed — no transactions confirmed. See console for details.' });
      }

      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error in batch mint:', error);
      notify({ type: 'error', message: error.message || 'Failed to batch mint tokens' });
    } finally {
      setIsLoading(false);
      setBatchProgress(null);
    }
  }, [publicKey, currentMintAddress, tokenState, batchInput, secureRPC, signAllTransactions, signTransaction, sendSignedTransaction, fetchTokenInfo]);

  const handlePauseToken = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    if (!tokenState.pausable) {
      notify({ type: 'error', message: 'This token does not have the Pausable extension' });
      return;
    }
    if (!tokenState.pausable.authority || !tokenState.pausable.authority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the pause authority for this token' });
      return;
    }
    if (tokenState.pausable.paused) {
      notify({ type: 'error', message: 'Token is already paused' });
      return;
    }

    setIsLoading(true);
    try {
      const transaction = new Transaction();
      transaction.add(createPauseInstruction(currentMintAddress, publicKey, [], tokenState.programId));
      const signature = await sendTransaction(transaction);
      notify({ type: 'success', message: 'Token paused', txid: signature });
      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error pausing token:', error);
      notify({ type: 'error', message: error.message || 'Failed to pause token' });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, sendTransaction, fetchTokenInfo]);

  const handleResumeToken = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    if (!tokenState.pausable) {
      notify({ type: 'error', message: 'This token does not have the Pausable extension' });
      return;
    }
    if (!tokenState.pausable.authority || !tokenState.pausable.authority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the pause authority for this token' });
      return;
    }
    if (!tokenState.pausable.paused) {
      notify({ type: 'error', message: 'Token is already active' });
      return;
    }

    setIsLoading(true);
    try {
      const transaction = new Transaction();
      transaction.add(createResumeInstruction(currentMintAddress, publicKey, [], tokenState.programId));
      const signature = await sendTransaction(transaction);
      notify({ type: 'success', message: 'Token resumed', txid: signature });
      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error resuming token:', error);
      notify({ type: 'error', message: error.message || 'Failed to resume token' });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, sendTransaction, fetchTokenInfo]);

  const handleForceTransfer = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    const delegate = tokenState.permanentDelegate?.delegate;
    if (!delegate) {
      notify({ type: 'error', message: 'This token has no active permanent delegate' });
      return;
    }
    if (!delegate.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the permanent delegate for this token' });
      return;
    }

    let sourceOwner: PublicKey;
    let destOwner: PublicKey;
    try {
      sourceOwner = new PublicKey(pdSourceOwner.trim());
    } catch {
      notify({ type: 'error', message: 'Invalid source holder address' });
      return;
    }
    try {
      destOwner = new PublicKey(pdDestOwner.trim());
    } catch {
      notify({ type: 'error', message: 'Invalid destination address' });
      return;
    }

    if (!pdTransferAmount || parseFloat(pdTransferAmount) <= 0) {
      notify({ type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    setIsLoading(true);
    try {
      const amount = BigInt(Math.floor(parseFloat(pdTransferAmount) * Math.pow(10, tokenState.decimals)));
      const sourceAta = await getAssociatedTokenAddress(
        currentMintAddress, sourceOwner, false, tokenState.programId, ASSOCIATED_TOKEN_PROGRAM_ID
      );
      const destAta = await getAssociatedTokenAddress(
        currentMintAddress, destOwner, false, tokenState.programId, ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const transaction = new Transaction();

      const destInfoResp = await secureRPC.getAccountInfo(destAta.toString());
      if (!destInfoResp?.value) {
        transaction.add(createAssociatedTokenAccountInstruction(
          publicKey, destAta, destOwner, currentMintAddress,
          tokenState.programId, ASSOCIATED_TOKEN_PROGRAM_ID,
        ));
      }

      transaction.add(createTransferCheckedInstruction(
        sourceAta,
        currentMintAddress,
        destAta,
        publicKey,
        amount,
        tokenState.decimals,
        [],
        tokenState.programId,
      ));

      const signature = await sendTransaction(transaction);
      notify({ type: 'success', message: `Force-transferred ${pdTransferAmount} tokens`, txid: signature });
      setPdSourceOwner("");
      setPdDestOwner("");
      setPdTransferAmount("");
      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error executing force transfer:', error);
      notify({ type: 'error', message: error.message || 'Failed to execute force transfer' });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, pdSourceOwner, pdDestOwner, pdTransferAmount, secureRPC, sendTransaction, fetchTokenInfo]);

  const handleForceBurn = useCallback(async () => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    const delegate = tokenState.permanentDelegate?.delegate;
    if (!delegate) {
      notify({ type: 'error', message: 'This token has no active permanent delegate' });
      return;
    }
    if (!delegate.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the permanent delegate for this token' });
      return;
    }

    let sourceOwner: PublicKey;
    try {
      sourceOwner = new PublicKey(pdBurnSourceOwner.trim());
    } catch {
      notify({ type: 'error', message: 'Invalid source holder address' });
      return;
    }

    if (!pdBurnAmount || parseFloat(pdBurnAmount) <= 0) {
      notify({ type: 'error', message: 'Please enter a valid amount' });
      return;
    }

    setIsLoading(true);
    try {
      const amount = BigInt(Math.floor(parseFloat(pdBurnAmount) * Math.pow(10, tokenState.decimals)));
      const sourceAta = await getAssociatedTokenAddress(
        currentMintAddress, sourceOwner, false, tokenState.programId, ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const transaction = new Transaction();
      transaction.add(createBurnCheckedInstruction(
        sourceAta,
        currentMintAddress,
        publicKey,
        amount,
        tokenState.decimals,
        [],
        tokenState.programId,
      ));

      const signature = await sendTransaction(transaction);
      notify({ type: 'success', message: `Force-burned ${pdBurnAmount} tokens`, txid: signature });
      setPdBurnSourceOwner("");
      setPdBurnAmount("");
      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error executing force burn:', error);
      notify({ type: 'error', message: error.message || 'Failed to execute force burn' });
    } finally {
      setIsLoading(false);
    }
  }, [publicKey, currentMintAddress, tokenState, pdBurnSourceOwner, pdBurnAmount, sendTransaction, fetchTokenInfo]);

  // Whitelist batch — thaws (whitelists) or freezes (de-whitelists) ATAs for
  // a list of holder wallets. Only meaningful when DefaultAccountState=Frozen
  // and freeze authority is still alive.
  const handleWhitelistBatch = useCallback(async (action: 'thaw' | 'freeze') => {
    if (!publicKey || !currentMintAddress || !tokenState) {
      notify({ type: 'error', message: 'Wallet not connected or token not loaded' });
      return;
    }
    if (!tokenState.freezeAuthority) {
      notify({ type: 'error', message: 'Freeze authority is revoked — whitelist actions are no longer possible.' });
      return;
    }
    if (!tokenState.freezeAuthority.equals(publicKey)) {
      notify({ type: 'error', message: 'You are not the freeze authority for this token' });
      return;
    }

    const { rows, errors } = parseBatchAddresses(whitelistInput);
    if (errors.length > 0) {
      notify({ type: 'error', message: `Fix ${errors.length} invalid row${errors.length === 1 ? '' : 's'} first` });
      return;
    }
    if (rows.length === 0) {
      notify({ type: 'error', message: 'Add at least one wallet address (one per line)' });
      return;
    }
    if (!signAllTransactions && !signTransaction) {
      notify({ type: 'error', message: 'This wallet cannot sign transactions' });
      return;
    }

    setIsLoading(true);
    setWhitelistProgress(null);
    try {
      const programId = tokenState.programId;

      // Pre-resolve all ATAs in parallel.
      const atas = await Promise.all(
        rows.map((owner) =>
          getAssociatedTokenAddress(currentMintAddress, owner, false, programId, ASSOCIATED_TOKEN_PROGRAM_ID),
        ),
      );

      // Classify every ATA on-chain in a single batched fetch (100 per RPC
      // call). SPL token account layout puts the state byte at offset 108
      // (mint 32 + owner 32 + amount 8 + delegateOption 4 + delegate 32) —
      // identical for Token-2022 since extensions live after offset 165. We
      // use this to skip wallets that are already in the target state, which
      // is what keeps simulation green (no Phantom malicious warning) and
      // prevents one already-correct wallet from rolling back its tx.
      const STATE_OFFSET = 108;
      const STATE_FROZEN = 2;
      type AtaStatus = 'missing' | 'frozen' | 'thawed';
      const statuses: AtaStatus[] = new Array(rows.length).fill('missing');
      for (let i = 0; i < atas.length; i += 100) {
        const chunk = atas.slice(i, i + 100);
        const resp = await secureRPC.callRPC('getMultipleAccounts', [
          chunk.map((a) => a.toString()),
          { encoding: 'base64', commitment: 'confirmed' },
        ]);
        const values: any[] = resp?.value || [];
        values.forEach((v, j) => {
          if (!v) { return; }
          const dataField = v.data;
          const base64Str = Array.isArray(dataField) ? dataField[0] : dataField;
          if (typeof base64Str !== 'string') { return; }
          const buf = Buffer.from(base64Str, 'base64');
          if (buf.length <= STATE_OFFSET) { return; }
          statuses[i + j] = buf.readUInt8(STATE_OFFSET) === STATE_FROZEN ? 'frozen' : 'thawed';
        });
      }

      let alreadyDone = 0;
      let skippedMissing = 0;
      const processable: { owner: PublicKey; ata: PublicKey; status: AtaStatus }[] = [];
      rows.forEach((owner, i) => {
        const status = statuses[i];
        if (action === 'thaw') {
          if (status === 'thawed') { alreadyDone++; return; }
          processable.push({ owner, ata: atas[i], status });
        } else {
          if (status === 'missing') { skippedMissing++; return; }
          if (status === 'frozen') { alreadyDone++; return; }
          processable.push({ owner, ata: atas[i], status });
        }
      });

      if (processable.length === 0) {
        const targetLabel = action === 'thaw' ? 'whitelisted' : 'frozen';
        const msg = action === 'freeze' && skippedMissing > 0 && alreadyDone === 0
          ? `Nothing to do — none of these ${rows.length} wallet${rows.length === 1 ? '' : 's'} have a token account for this mint yet.`
          : `Nothing to do — all ${rows.length} wallet${rows.length === 1 ? '' : 's'} are already ${targetLabel}.`;
        notify({ type: 'info', message: msg });
        setIsLoading(false);
        return;
      }

      // Build instruction groups per holder. Idempotent ATA create only when
      // missing — saves rent fees when the account already exists. Group
      // atomicity is preserved by the tx packer.
      const groups: TransactionInstruction[][] = processable.map(({ owner, ata, status }) => {
        if (action === 'thaw') {
          if (status === 'missing') {
            return [
              createAssociatedTokenAccountIdempotentInstruction(
                publicKey, ata, owner, currentMintAddress, programId, ASSOCIATED_TOKEN_PROGRAM_ID,
              ),
              createThawAccountInstruction(ata, currentMintAddress, publicKey, [], programId),
            ];
          }
          return [createThawAccountInstruction(ata, currentMintAddress, publicKey, [], programId)];
        }
        return [createFreezeAccountInstruction(ata, currentMintAddress, publicKey, [], programId)];
      });

      const { blockhash, lastValidBlockHeight } = await secureRPC.getLatestBlockhash('finalized');
      const freshTx = () => {
        const tx = new Transaction();
        tx.recentBlockhash = blockhash;
        tx.lastValidBlockHeight = lastValidBlockHeight;
        tx.feePayer = publicKey;
        return tx;
      };

      const txs: Transaction[] = [];
      let current = freshTx();
      for (const group of groups) {
        const trial = freshTx();
        trial.add(...current.instructions, ...group);
        const size = trial.serialize({ requireAllSignatures: false, verifySignatures: false }).length;
        if (current.instructions.length > 0 && size > MAX_TX_BYTES) {
          txs.push(current);
          current = freshTx();
        }
        current.add(...group);
      }
      if (current.instructions.length > 0) { txs.push(current); }

      let signed: Transaction[];
      if (signAllTransactions) {
        signed = await signAllTransactions(txs);
      } else if (signTransaction) {
        signed = [];
        for (const tx of txs) {
          // eslint-disable-next-line no-await-in-loop
          signed.push(await signTransaction(tx));
        }
      } else {
        throw new Error('This wallet cannot sign transactions');
      }

      const sigs: string[] = [];
      const failures: { index: number; error: string }[] = [];
      for (let i = 0; i < signed.length; i++) {
        setWhitelistProgress({ current: i + 1, total: signed.length });
        try {
          // eslint-disable-next-line no-await-in-loop
          const sig = await sendSignedTransaction(signed[i]);
          sigs.push(sig);
        } catch (e: any) {
          failures.push({ index: i + 1, error: e?.message || 'unknown error' });
        }
      }

      const verb = action === 'thaw' ? 'Whitelisted' : 'Frozen';
      const skipParts: string[] = [];
      if (alreadyDone > 0) {
        const label = action === 'thaw' ? 'already whitelisted' : 'already frozen';
        skipParts.push(`${alreadyDone} ${label}`);
      }
      if (skippedMissing > 0) {
        skipParts.push(`${skippedMissing} without a token account`);
      }
      const skipNote = skipParts.length > 0 ? ` (skipped ${skipParts.join(', ')})` : '';
      if (failures.length === 0) {
        notify({
          type: 'success',
          message: `${verb} ${processable.length} wallet${processable.length === 1 ? '' : 's'} across ${sigs.length} transaction${sigs.length === 1 ? '' : 's'}${skipNote}`,
          txid: sigs[sigs.length - 1],
        });
        setWhitelistInput("");
      } else if (sigs.length > 0) {
        console.error('Whitelist batch — failed transactions:', failures);
        notify({
          type: 'info',
          message: `${sigs.length}/${signed.length} transactions confirmed${skipNote}. ${failures.length} failed — see console for details.`,
        });
      } else {
        console.error('Whitelist batch — failed transactions:', failures);
        notify({ type: 'error', message: 'Whitelist batch failed — no transactions confirmed. See console for details.' });
      }

      await fetchTokenInfo();
    } catch (error: any) {
      console.error('Error in whitelist batch:', error);
      notify({ type: 'error', message: error.message || 'Failed to update whitelist' });
    } finally {
      setIsLoading(false);
      setWhitelistProgress(null);
    }
  }, [publicKey, currentMintAddress, tokenState, whitelistInput, secureRPC, signAllTransactions, signTransaction, sendSignedTransaction, fetchTokenInfo]);

  // The signing message includes the mint, so the derived secret is mint-bound.
  // Clear it whenever the loaded mint changes to avoid showing a stale derived
  // pubkey alongside a different token.
  useEffect(() => {
    setAuditorSecret(null);
    setAuditorPubkeyBytes(null);
    setAuditorTxSig("");
    setAuditorResult(null);
    setPdSourceOwner("");
    setPdDestOwner("");
    setPdTransferAmount("");
    setPdBurnSourceOwner("");
    setPdBurnAmount("");
    setMintAmount("");
    setMintRecipient("");
    setBatchInput("");
    setBatchProgress(null);
    setWhitelistInput("");
    setWhitelistProgress(null);
    setWhitelistMode('thaw');
  }, [currentMintAddress]);

  const handleDeriveAuditorKey = useCallback(async () => {
    if (!publicKey || !signMessage || !currentMintAddress) {
      notify({ type: 'error', message: 'Connect a Solana wallet that supports signMessage (most browser-extension wallets do).' });
      return;
    }
    setAuditorBusy(true);
    setAuditorResult(null);
    try {
      const elgamal = await import('../utils/elgamal');
      const message = elgamal.buildElGamalSignMessage(currentMintAddress);
      const signature = await signMessage(message);
      const secret = elgamal.deriveElGamalSecret(signature);
      const pubkey = elgamal.computeElGamalPubkey(secret);
      setAuditorSecret(secret);
      setAuditorPubkeyBytes(pubkey);
      notify({ type: 'success', message: 'Auditor key derived locally — never sent anywhere.' });
    } catch (err: any) {
      console.error('Auditor key derivation failed:', err);
      notify({ type: 'error', message: err?.message || 'Failed to derive auditor key.' });
    } finally {
      setAuditorBusy(false);
    }
  }, [publicKey, signMessage, currentMintAddress]);

  const handleAuditorDecrypt = useCallback(async () => {
    if (!auditorSecret) {
      notify({ type: 'error', message: 'Derive your auditor key first.' });
      return;
    }
    const sig = auditorTxSig.trim();
    if (!sig) {
      notify({ type: 'error', message: 'Paste the transaction signature you want to decrypt.' });
      return;
    }
    setAuditorBusy(true);
    setAuditorResult(null);
    try {
      const elgamal = await import('../utils/elgamal');
      // jsonParsed gives us a friendly view but bytes need to come from raw
      // so we ask for `base64` encoding and decode each instruction's `data`.
      const tx = await secureRPC.callRPC('getTransaction', [
        sig,
        { encoding: 'base64', commitment: 'confirmed', maxSupportedTransactionVersion: 0 },
      ]);
      if (!tx) {
        setAuditorResult({ ok: false, error: 'Transaction not found. Verify the signature and that your RPC has it indexed.' });
        return;
      }
      // tx.transaction is [base64, encoding] under base64 encoding.
      const txBase64: string = Array.isArray(tx.transaction) ? tx.transaction[0] : tx.transaction;
      const txBytes = new Uint8Array(Buffer.from(txBase64, 'base64'));
      // Cheap pass: scan the entire tx bytes (header + msg + instructions) for
      // grouped-ciphertext patterns. This covers the case where ciphertexts live
      // in multiple instructions (transfer + proof) without us having to know
      // which program owns which instruction.
      const candidates = elgamal.scanInstructionsForGroupedCiphertexts([{ data: txBytes }]);
      if (candidates.length === 0) {
        setAuditorResult({ ok: false, error: 'No grouped ElGamal ciphertexts found in this transaction. Either the tx is not a confidential transfer, or it uses a layout we do not yet recognize.' });
        return;
      }
      const result = elgamal.decryptFirstAuditorAmount(auditorSecret, candidates);
      if (!result) {
        setAuditorResult({ ok: false, error: 'Could not decrypt: either the auditor key on this transfer was different (auditor was changed since), or the layout has the auditor handle at a non-standard index.' });
        return;
      }
      setAuditorResult({ ok: true, amount: result.amount });
    } catch (err: any) {
      console.error('Auditor decrypt failed:', err);
      setAuditorResult({ ok: false, error: err?.message || 'Decrypt failed.' });
    } finally {
      setAuditorBusy(false);
    }
  }, [auditorSecret, auditorTxSig, secureRPC]);

  const canRevokeMint = tokenState?.mintAuthority && publicKey && tokenState.mintAuthority.equals(publicKey);
  const canRevokeFreeze = tokenState?.freezeAuthority && publicKey && tokenState.freezeAuthority.equals(publicKey);
  // Metaplex PDAs revoke via updateMetadataAccountV2; Token-2022 embedded
  // metadata revokes via the token-metadata UpdateAuthority instruction.
  const canRevokeUpdateAuthority = tokenState?.metadataExists
    && tokenState?.metadataSource !== 'none'
    && tokenState?.metadataUpdateAuthority
    && publicKey
    && tokenState.metadataUpdateAuthority.equals(publicKey);
  const canMint = tokenState?.mintAuthority && publicKey && tokenState.mintAuthority.equals(publicKey);
  const isPauseAuthority = !!(tokenState?.pausable?.authority && publicKey && tokenState.pausable.authority.equals(publicKey));
  const isPermanentDelegate = !!(tokenState?.permanentDelegate?.delegate && publicKey && tokenState.permanentDelegate.delegate.equals(publicKey));

  const batchParsed = useMemo(() => parseBatchRecipients(batchInput), [batchInput]);
  const batchTotal = useMemo(
    () => batchParsed.rows.reduce((sum, r) => sum + parseFloat(r.amountRaw), 0),
    [batchParsed]
  );

  const whitelistParsed = useMemo(() => parseBatchAddresses(whitelistInput), [whitelistInput]);
  const isWhitelistMint = !!(
    tokenState?.defaultAccountState
    && tokenState.defaultAccountState.state === AccountState.Frozen
  );
  const isFreezeAuthority = !!(
    tokenState?.freezeAuthority && publicKey && tokenState.freezeAuthority.equals(publicKey)
  );

  const snapshotStatus = useMemo(() => {
    if (!tokenState) {return { label: '', tone: 'muted' as const };}
    const hasAnyLive = tokenState.mintAuthority || tokenState.freezeAuthority || (tokenState.metadataExists && tokenState.metadataUpdateAuthority);
    return hasAnyLive
      ? { label: 'Authorities live', tone: 'warn' as const }
      : { label: 'Fully immutable', tone: 'ok' as const };
  }, [tokenState]);

  const faqs = MANAGE_TOKEN_FAQS;

  // Single per-authority card: combines current state, address, and the
  // revoke button. Replaces the old split between authSignal (display) and
  // actionCard (button) — those used to duplicate the same authority field.
  const renderAuthorityCard = (params: {
    name: string;
    title: string;
    description: string;
    addr: PublicKey | null;
    metadataExists?: boolean;
    onRevoke: () => void;
    canRevoke: boolean;
    notOwnerCopy: string;
  }) => {
    const { name, title, description, addr, metadataExists, onRevoke, canRevoke, notOwnerCopy } = params;
    const noMetadata = metadataExists === false;
    const revoked = !addr && !noMetadata;
    const isYou = !!(addr && publicKey && addr.equals(publicKey));

    const stateClass = noMetadata
      ? styles.stateNone
      : revoked
      ? styles.stateRevoked
      : isYou
      ? styles.stateYou
      : styles.stateActive;

    const stateLabel = noMetadata
      ? 'No metadata'
      : revoked
      ? '✓ Revoked'
      : isYou
      ? '● You control'
      : '● Active · external';

    const copyKey = `auth-${name}`;
    const addrStr = addr ? addr.toString() : '';

    return (
      <div className={`${styles.authCard} ${stateClass}`}>
        <div className={styles.authHead}>
          <span className={styles.authState}>{stateLabel}</span>
        </div>
        <h3 className={styles.authTitle}>{title}</h3>
        {addr ? (
          <p className={styles.authAddr}>
            <span>{shortAddr(addrStr, 6, 6)}</span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() => copyToClipboard(addrStr, copyKey)}
              aria-label={`Copy ${title} address`}
              title="Copy address"
            >
              <CopyIcon />
            </button>
            {copiedKey === copyKey && <strong style={{ color: '#14F195', fontSize: '0.7rem' }}>copied</strong>}
          </p>
        ) : (
          <p className={styles.authAddr} style={{ color: 'rgba(248,249,250,0.5)' }}>
            {noMetadata ? 'Mint has no metadata account' : 'Authority is null'}
          </p>
        )}
        <p className={styles.authDesc}>{description}</p>
        <div className={styles.authFooter}>
          {noMetadata ? (
            <div className={styles.noneBadge}>Not applicable</div>
          ) : revoked ? (
            <div className={styles.revokedBadge}>Already revoked · immutable</div>
          ) : (
            <>
              <button
                className={styles.dangerBtn}
                onClick={onRevoke}
                disabled={isLoading || !canRevoke || !publicKey}
              >
                {isLoading ? <ClipLoader size={14} color="#FFD6DF" /> : 'Execute revoke'}
              </button>
              {!canRevoke && (
                <p className={styles.authHint}>{notOwnerCopy}</p>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.gridLines} aria-hidden />
      <div className={styles.grain} aria-hidden />

      <div className={styles.content}>
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link href="/">Home</Link>
          <span>/</span>
          <span className={styles.breadcrumbCurrent}>Manage Token</span>
        </nav>

        {/* Compact header */}
        <div className={styles.headerRow}>
          <h1 className={styles.headerTitle}>
            Manage your <span>SPL token</span>
          </h1>
          <span className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            Authority Console · Mainnet-beta
          </span>
        </div>
        <p className={styles.headerLede}>
          A read-only scanner and authority console for any Solana token. Paste a mint to inspect live authorities, then revoke mint, freeze and update — or mint more supply while you still hold the keys.
        </p>

        {/* The tool — command bar */}
        <div className={styles.commandBar}>
          <div className={styles.commandChrome}>
            <span className={styles.commandDot} />
            <span className={styles.commandDot} />
            <span className={styles.commandDot} />
          </div>
          <div className={styles.commandInput}>
            <span className={styles.commandPrompt}>Mint address</span>
            <input
              className={styles.commandField}
              type="text"
              value={mintAddressInput}
              onChange={(e) => setMintAddressInput(e.target.value)}
              placeholder="Paste SPL token mint address (So11111111…)"
              disabled={isFetching}
              spellCheck={false}
              aria-label="Token mint address"
              onKeyDown={(e) => { if (e.key === 'Enter' && mintAddressInput.trim() && !isFetching) {fetchTokenInfo();} }}
            />
            <button
              className={styles.primaryBtn}
              onClick={fetchTokenInfo}
              disabled={isFetching || !mintAddressInput.trim()}
            >
              {isFetching ? <ClipLoader size={14} color="#0A0F1C" /> : <>Scan<ArrowIcon /></>}
            </button>
          </div>
          <p className={styles.commandHint}>
            <strong>Read-only — no signature required.</strong> Works for Token-2022 mints too. Connect a wallet to revoke or mint.
          </p>
        </div>

        {!publicKey && (
          <div className={styles.walletNotice}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
              <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
              <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
            </svg>
            <span>Connect your wallet to perform revocations or mint new supply.</span>
          </div>
        )}

        {/* Empty state */}
        {!tokenState && !isFetching && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon} aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>
            <p><strong>No token loaded.</strong> Paste a mint address above to reveal its authority surface.</p>
          </div>
        )}

        {/* Token loaded */}
        {tokenState && currentMintAddress && (
          <>
            {/* Snapshot — compact 3-card row */}
            <section className={`${styles.section} ${styles.snapshot}`} aria-label="Token snapshot">
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Token snapshot</h2>
                <span
                  className={`${styles.sectionTagline} ${
                    snapshotStatus.tone === 'ok' ? styles.okTagline : styles.warnTagline
                  }`}
                >
                  {snapshotStatus.label}
                </span>
              </div>

              <div className={styles.snapshotGrid}>
                <div className={styles.snapshotCard}>
                  <span className={styles.snapshotLabel}>Mint address</span>
                  <div className={styles.addressRow}>
                    <p className={styles.monoAddress}>{currentMintAddress.toString()}</p>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={() => copyToClipboard(currentMintAddress.toString(), 'mint')}
                      aria-label="Copy mint address"
                      title="Copy mint address"
                    >
                      <CopyIcon />
                    </button>
                  </div>
                  <p className={styles.addressTag}>
                    {copiedKey === 'mint' ? <strong>Copied to clipboard</strong> : <>Program · {tokenState.programId.equals(TOKEN_2022_PROGRAM_ID) ? 'Token-2022' : 'SPL Token'}</>}
                  </p>
                </div>

                <div className={styles.snapshotCard}>
                  <span className={styles.snapshotLabel}>Total supply</span>
                  <p className={styles.bigNumber}>
                    {(Number(tokenState.supply) / Math.pow(10, tokenState.decimals)).toLocaleString()}
                  </p>
                  <span className={styles.smallUnit}>{tokenState.decimals} decimals</span>
                </div>

                <div className={styles.snapshotCard}>
                  <span className={styles.snapshotLabel}>Status</span>
                  <div className={styles.statusStack}>
                    <span className={`${styles.pill} ${tokenState.mintAuthority ? styles.pillWarn : styles.pillOk}`}>
                      <span className={styles.pillDot} />
                      Mint {tokenState.mintAuthority ? 'live' : 'locked'}
                    </span>
                    <span className={`${styles.pill} ${tokenState.freezeAuthority ? styles.pillWarn : styles.pillOk}`}>
                      <span className={styles.pillDot} />
                      Freeze {tokenState.freezeAuthority ? 'live' : 'locked'}
                    </span>
                    <span className={`${styles.pill} ${
                      !tokenState.metadataExists ? styles.pillMuted
                      : tokenState.metadataUpdateAuthority ? styles.pillWarn : styles.pillOk
                    }`}>
                      <span className={styles.pillDot} />
                      Meta {
                        !tokenState.metadataExists ? 'none'
                        : tokenState.metadataUpdateAuthority ? 'mutable' : 'frozen'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Authorities — merged display + revoke action */}
            <section className={styles.section} aria-label="Authorities">
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Authorities</h2>
                <span className={`${styles.sectionTagline} ${styles.dangerTagline}`}>Irreversible</span>
              </div>

              <div className={styles.dangerZone}>
                <div className={styles.dangerInner}>
                  <div className={styles.dangerNote} role="alert">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span><strong>Irreversible operations.</strong> Revoking cannot be undone — even by the original wallet. Review every field before signing.</span>
                  </div>

                  <div className={styles.authGrid}>
                    {renderAuthorityCard({
                      name: 'mint_authority',
                      title: 'Mint authority',
                      description: 'Locks total supply forever. No one — not even you — can ever create additional tokens.',
                      addr: tokenState.mintAuthority,
                      onRevoke: handleRevokeMintAuthority,
                      canRevoke: !!canRevokeMint,
                      notOwnerCopy: 'You are not the mint authority.',
                    })}

                    {renderAuthorityCard({
                      name: 'freeze_authority',
                      title: 'Freeze authority',
                      description: 'Guarantees holders can always transfer. Removes the ability to freeze any token account.',
                      addr: tokenState.freezeAuthority,
                      onRevoke: handleRevokeFreezeAuthority,
                      canRevoke: !!canRevokeFreeze,
                      notOwnerCopy: 'You are not the freeze authority.',
                    })}

                    {renderAuthorityCard({
                      name: 'update_authority',
                      title: 'Update authority',
                      description: 'Seals the name, symbol, image and URI. Metadata becomes permanently immutable.',
                      addr: tokenState.metadataExists ? tokenState.metadataUpdateAuthority : null,
                      metadataExists: tokenState.metadataExists,
                      onRevoke: handleRevokeUpdateAuthority,
                      canRevoke: !!canRevokeUpdateAuthority,
                      notOwnerCopy: 'You are not the update authority.',
                    })}
                  </div>
                </div>
              </div>
            </section>

            {/* Mint additional tokens */}
            <section className={styles.section} aria-label="Mint additional tokens">
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle}>Mint additional tokens</h2>
                <span className={`${styles.sectionTagline} ${styles.okTagline}`}>
                  {tokenState.mintAuthority ? 'Expansion · reversible flow' : 'Supply is locked'}
                </span>
              </div>
              <div className={styles.toolCard}>
                <div className={styles.toolInner}>
                  <div className={styles.toolHead}>
                    <div>
                      <h3 className={styles.toolHeadTitle}>Increase supply</h3>
                      <p className={styles.toolHeadSub}>
                        Mint new tokens to the connected wallet, a different wallet, or many wallets at once. Requires mint authority.
                      </p>
                    </div>
                    <span className={styles.toolBadge}>+ Mint</span>
                  </div>

                  <div className={styles.mintModeTabs} role="tablist" aria-label="Mint mode">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mintMode === 'single'}
                      className={`${styles.mintModeTab} ${mintMode === 'single' ? styles.mintModeTabActive : ''}`}
                      onClick={() => setMintMode('single')}
                      disabled={isLoading}
                    >
                      Single wallet
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mintMode === 'batch'}
                      className={`${styles.mintModeTab} ${mintMode === 'batch' ? styles.mintModeTabActive : ''}`}
                      onClick={() => setMintMode('batch')}
                      disabled={isLoading}
                    >
                      Multiple wallets
                    </button>
                  </div>

                  {mintMode === 'single' ? (
                    <>
                      <div className={styles.toolInputWrap} style={{ marginBottom: '0.5rem' }}>
                        <input
                          className={styles.toolInput}
                          type="text"
                          value={mintRecipient}
                          onChange={(e) => setMintRecipient(e.target.value)}
                          placeholder="Recipient wallet — leave blank for connected wallet"
                          spellCheck={false}
                          disabled={isLoading || !canMint}
                          aria-label="Recipient wallet address"
                        />
                      </div>
                      <div className={styles.toolForm}>
                        <div className={styles.toolInputWrap}>
                          <input
                            className={styles.toolInput}
                            type="number"
                            value={mintAmount}
                            onChange={(e) => setMintAmount(e.target.value)}
                            placeholder="0.00"
                            step="0.000000001"
                            min="0"
                            disabled={isLoading || !canMint}
                            aria-label="Amount to mint"
                          />
                          <span className={styles.toolInputSuffix}>tokens</span>
                        </div>
                        <button
                          className={styles.toolBtn}
                          onClick={handleMintAdditionalTokens}
                          disabled={isLoading || !canMint || !mintAmount || !publicKey}
                        >
                          {isLoading ? <ClipLoader size={14} color="#0A0F1C" /> : <>Mint now<ArrowIcon /></>}
                        </button>
                      </div>
                      {mintRecipient.trim() && canMint && (
                        <p className={styles.toolFootnote}>
                          Minting to an external wallet. Its associated token account is created automatically if missing — you pay the rent (~0.002 SOL).
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className={styles.toolHeadSub} style={{ marginBottom: '0.5rem' }}>
                        One recipient per line as <strong>address, amount</strong>. Paste below or upload a CSV.
                      </p>
                      <textarea
                        className={styles.toolTextarea}
                        value={batchInput}
                        onChange={(e) => setBatchInput(e.target.value)}
                        placeholder={"7xKq...wallet1, 100\n9aPz...wallet2, 250.5\n3mNb...wallet3, 1000"}
                        spellCheck={false}
                        rows={6}
                        disabled={isLoading || !canMint}
                        aria-label="Batch recipients list"
                      />
                      <div className={styles.batchActions}>
                        <label className={styles.batchUploadBtn}>
                          Upload CSV
                          <input
                            type="file"
                            accept=".csv,text/csv,text/plain"
                            style={{ display: 'none' }}
                            disabled={isLoading || !canMint}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) { return; }
                              const reader = new FileReader();
                              reader.onload = () => {
                                const text = typeof reader.result === 'string' ? reader.result : '';
                                setBatchInput((prev) => (prev.trim() ? `${prev.trim()}\n${text.trim()}` : text.trim()));
                              };
                              reader.onerror = () => notify({ type: 'error', message: 'Could not read that file' });
                              reader.readAsText(file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        {batchInput.trim() && (
                          <button
                            type="button"
                            className={styles.batchClearBtn}
                            onClick={() => setBatchInput("")}
                            disabled={isLoading}
                          >
                            Clear
                          </button>
                        )}
                      </div>

                      {batchInput.trim() && (
                        <div className={styles.batchPreview}>
                          <div className={styles.batchPreviewRow}>
                            <span>Valid recipients</span>
                            <strong>{batchParsed.rows.length}</strong>
                          </div>
                          <div className={styles.batchPreviewRow}>
                            <span>Total to mint</span>
                            <strong>
                              {batchTotal.toLocaleString(undefined, { maximumFractionDigits: tokenState.decimals })} tokens
                            </strong>
                          </div>
                          {batchParsed.errors.length > 0 && (
                            <div className={styles.batchErrors}>
                              {batchParsed.errors.slice(0, 5).map((err) => (
                                <p key={err}>{err}</p>
                              ))}
                              {batchParsed.errors.length > 5 && (
                                <p>…and {batchParsed.errors.length - 5} more</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <button
                        className={styles.toolBtn}
                        style={{ width: '100%', marginTop: '0.75rem' }}
                        onClick={handleBatchMint}
                        disabled={
                          isLoading || !canMint || !publicKey ||
                          batchParsed.rows.length === 0 || batchParsed.errors.length > 0
                        }
                      >
                        {isLoading ? (
                          batchProgress
                            ? <>Sending {batchProgress.current}/{batchProgress.total}…</>
                            : <ClipLoader size={14} color="#0A0F1C" />
                        ) : (
                          <>
                            Mint to {batchParsed.rows.length || ''} wallet{batchParsed.rows.length === 1 ? '' : 's'}
                            <ArrowIcon />
                          </>
                        )}
                      </button>
                      <p className={styles.toolFootnote}>
                        Recipients are packed into one or more transactions and signed together. Missing token accounts are created automatically — you pay ~0.002 SOL rent each. Max {MAX_BATCH_RECIPIENTS} recipients per batch.
                      </p>
                    </>
                  )}

                  {!canMint && tokenState.mintAuthority && (
                    <p className={styles.toolFootnote}>You are not the mint authority for this token.</p>
                  )}
                  {!tokenState.mintAuthority && (
                    <p className={`${styles.toolFootnote} ${styles.ok}`}>Mint authority is revoked — supply is permanently fixed.</p>
                  )}
                </div>
              </div>
            </section>

            {/* Pausable — Token-2022 only */}
            {tokenState.pausable && (
              <section className={styles.section} aria-label="Pause or resume token">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>{tokenState.pausable.paused ? 'Resume token' : 'Pause token'}</h2>
                  <span className={`${styles.sectionTagline} ${tokenState.pausable.paused ? styles.warnTagline : styles.okTagline}`}>
                    Status · {tokenState.pausable.paused ? 'PAUSED' : 'ACTIVE'}
                  </span>
                </div>
                <div className={`${styles.toolCard} ${styles.warnVariant}`}>
                  <div className={styles.toolInner}>
                    <div className={styles.toolHead}>
                      <div>
                        <h3 className={styles.toolHeadTitle}>
                          {tokenState.pausable.paused ? 'Resume all transfers, mints and burns' : 'Halt all transfers, mints and burns'}
                        </h3>
                        <p className={styles.toolHeadSub}>
                          {tokenState.pausable.paused
                            ? 'Token is currently paused — all transfers, mints, and burns are blocked. Resume to restore activity.'
                            : 'Reversible by the pause authority via Resume. Mint-wide effect.'}
                        </p>
                      </div>
                      <span className={styles.toolBadge}>{tokenState.pausable.paused ? 'Frozen' : 'Live'}</span>
                    </div>

                    <button
                      className={`${styles.toolBtn} ${styles.warnBtn}`}
                      onClick={tokenState.pausable.paused ? handleResumeToken : handlePauseToken}
                      disabled={isLoading || !isPauseAuthority || !publicKey}
                      style={{ width: '100%' }}
                    >
                      {isLoading
                        ? <ClipLoader size={14} color="#0A0F1C" />
                        : <>{tokenState.pausable.paused ? 'Resume now' : 'Pause now'}<ArrowIcon /></>}
                    </button>

                    <p className={styles.toolFootnote}>
                      Pause authority: <strong>{tokenState.pausable.authority ? shortAddr(tokenState.pausable.authority.toString(), 6, 6) : 'Revoked (none)'}</strong>
                    </p>
                    {!tokenState.pausable.authority && (
                      <p className={`${styles.toolFootnote} ${styles.ok}`}>Pause authority is revoked — this token can never be paused again.</p>
                    )}
                    {tokenState.pausable.authority && !isPauseAuthority && (
                      <p className={styles.toolFootnote}>You are not the pause authority for this token.</p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Whitelist management — Token-2022 with DefaultAccountState=Frozen */}
            {isWhitelistMint && (
              <section className={styles.section} aria-label="Whitelist management">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>Whitelist management</h2>
                  <span className={`${styles.sectionTagline} ${tokenState.freezeAuthority ? styles.warnTagline : styles.okTagline}`}>
                    {tokenState.freezeAuthority ? 'DefaultAccountState · Frozen' : 'Freeze authority revoked — locked'}
                  </span>
                </div>
                <div className={`${styles.toolCard} ${styles.warnVariant}`}>
                  <div className={styles.toolInner}>
                    <div className={styles.toolHead}>
                      <div>
                        <h3 className={styles.toolHeadTitle}>
                          {whitelistMode === 'thaw' ? 'Whitelist holders (thaw accounts)' : 'De-whitelist holders (freeze accounts)'}
                        </h3>
                        <p className={styles.toolHeadSub}>
                          New token accounts on this mint are created <strong>frozen</strong> by default. As the freeze authority you can thaw specific wallets to let them hold and transfer, or refreeze previously-whitelisted ones. Missing token accounts are created and thawed in the same transaction.
                        </p>
                      </div>
                      <span className={styles.toolBadge}>{whitelistMode === 'thaw' ? 'Add' : 'Remove'}</span>
                    </div>

                    <div className={styles.mintModeTabs} role="tablist" aria-label="Whitelist mode">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={whitelistMode === 'thaw'}
                        className={`${styles.mintModeTab} ${whitelistMode === 'thaw' ? styles.mintModeTabActive : ''}`}
                        onClick={() => setWhitelistMode('thaw')}
                        disabled={isLoading}
                      >
                        Whitelist (thaw)
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={whitelistMode === 'freeze'}
                        className={`${styles.mintModeTab} ${whitelistMode === 'freeze' ? styles.mintModeTabActive : ''}`}
                        onClick={() => setWhitelistMode('freeze')}
                        disabled={isLoading}
                      >
                        Freeze (de-whitelist)
                      </button>
                    </div>

                    <p className={styles.toolHeadSub} style={{ marginBottom: '0.5rem' }}>
                      One wallet address per line. Paste below or upload a CSV.
                    </p>
                    <textarea
                      className={styles.toolTextarea}
                      value={whitelistInput}
                      onChange={(e) => setWhitelistInput(e.target.value)}
                      placeholder={"7xKq...wallet1\n9aPz...wallet2\n3mNb...wallet3"}
                      spellCheck={false}
                      rows={6}
                      disabled={isLoading || !isFreezeAuthority}
                      aria-label="Whitelist wallets"
                    />
                    <div className={styles.batchActions}>
                      <label className={styles.batchUploadBtn}>
                        Upload CSV
                        <input
                          type="file"
                          accept=".csv,text/csv,text/plain"
                          style={{ display: 'none' }}
                          disabled={isLoading || !isFreezeAuthority}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) { return; }
                            const reader = new FileReader();
                            reader.onload = () => {
                              const text = typeof reader.result === 'string' ? reader.result : '';
                              setWhitelistInput((prev) => (prev.trim() ? `${prev.trim()}\n${text.trim()}` : text.trim()));
                            };
                            reader.onerror = () => notify({ type: 'error', message: 'Could not read that file' });
                            reader.readAsText(file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      {whitelistInput.trim() && (
                        <button
                          type="button"
                          className={styles.batchClearBtn}
                          onClick={() => setWhitelistInput("")}
                          disabled={isLoading}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {whitelistInput.trim() && (
                      <div className={styles.batchPreview}>
                        <div className={styles.batchPreviewRow}>
                          <span>Valid wallets</span>
                          <strong>{whitelistParsed.rows.length}</strong>
                        </div>
                        {whitelistParsed.errors.length > 0 && (
                          <div className={styles.batchErrors}>
                            {whitelistParsed.errors.slice(0, 5).map((err) => (
                              <p key={err}>{err}</p>
                            ))}
                            {whitelistParsed.errors.length > 5 && (
                              <p>…and {whitelistParsed.errors.length - 5} more</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      className={`${styles.toolBtn} ${styles.warnBtn}`}
                      style={{ width: '100%', marginTop: '0.75rem' }}
                      onClick={() => handleWhitelistBatch(whitelistMode)}
                      disabled={
                        isLoading || !isFreezeAuthority || !publicKey ||
                        whitelistParsed.rows.length === 0 || whitelistParsed.errors.length > 0
                      }
                    >
                      {isLoading ? (
                        whitelistProgress
                          ? <>Sending {whitelistProgress.current}/{whitelistProgress.total}…</>
                          : <ClipLoader size={14} color="#0A0F1C" />
                      ) : (
                        <>
                          {whitelistMode === 'thaw' ? 'Whitelist' : 'Freeze'} {whitelistParsed.rows.length || ''} wallet{whitelistParsed.rows.length === 1 ? '' : 's'}
                          <ArrowIcon />
                        </>
                      )}
                    </button>

                    <p className={styles.toolFootnote}>
                      Freeze authority: <strong>{tokenState.freezeAuthority ? shortAddr(tokenState.freezeAuthority.toString(), 6, 6) : 'Revoked (none)'}</strong>
                      {tokenState.freezeAuthority && isFreezeAuthority && (
                        <> · <strong style={{ color: '#14F195' }}>You control</strong></>
                      )}
                    </p>
                    {!tokenState.freezeAuthority && (
                      <p className={`${styles.toolFootnote} ${styles.err}`}>Freeze authority is revoked — the whitelist is permanently sealed at its current membership.</p>
                    )}
                    {tokenState.freezeAuthority && !isFreezeAuthority && (
                      <p className={styles.toolFootnote}>You are not the freeze authority for this token.</p>
                    )}
                    {whitelistMode === 'thaw' ? (
                      <p className={styles.toolFootnote}>
                        Each wallet&apos;s on-chain state is checked first — already-whitelisted wallets are skipped, missing accounts are created + thawed in one transaction, and frozen ones are thawed. You only pay ATA rent (~0.002 SOL) for genuinely new wallets. Max {MAX_BATCH_RECIPIENTS} wallets per batch.
                      </p>
                    ) : (
                      <p className={styles.toolFootnote}>
                        Each wallet&apos;s on-chain state is checked first — already-frozen wallets and wallets without a token account are skipped silently, so the transaction always simulates cleanly. Max {MAX_BATCH_RECIPIENTS} wallets per batch.
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Permanent delegate — Token-2022 only, extension present */}
            {tokenState.permanentDelegate && (
              <section className={styles.section} aria-label="Permanent delegate">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>Permanent delegate</h2>
                  <span className={`${styles.sectionTagline} ${styles.dangerTagline}`}>
                    {tokenState.permanentDelegate.delegate ? 'Force-transfer & burn rights' : 'No active delegate'}
                  </span>
                </div>
                <div className={`${styles.toolCard} ${styles.warnVariant}`}>
                  <div className={styles.toolInner}>
                    <div className={styles.toolHead}>
                      <div>
                        <h3 className={styles.toolHeadTitle}>Exercise permanent delegate rights</h3>
                        <p className={styles.toolHeadSub}>
                          The permanent delegate can move or burn tokens from any holder&apos;s account without their signature. Targets the holder&apos;s associated token account (ATA).
                        </p>
                      </div>
                      <span className={styles.toolBadge}>Delegate</span>
                    </div>

                    <p className={styles.toolFootnote}>
                      Configured delegate: <strong>{tokenState.permanentDelegate.delegate ? shortAddr(tokenState.permanentDelegate.delegate.toString(), 6, 6) : 'None'}</strong>
                      {tokenState.permanentDelegate.delegate && isPermanentDelegate && (
                        <> · <strong style={{ color: '#14F195' }}>You control</strong></>
                      )}
                    </p>

                    {!tokenState.permanentDelegate.delegate ? (
                      <p className={`${styles.toolFootnote} ${styles.ok}`} style={{ marginTop: '0.75rem' }}>
                        Permanent delegate is set to the zero address — nobody can force-transfer or burn from holders on this mint.
                      </p>
                    ) : (
                      <>
                        <p className={`${styles.toolFootnote} ${styles.err}`} style={{ marginTop: '0.75rem' }}>
                          <strong>Destructive & irreversible.</strong> These actions take or destroy tokens from another wallet without consent. Use only when you have a clear legal or operational basis (regulator order, compliance recovery, internal treasury moves).
                        </p>

                        {/* Force transfer */}
                        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,179,71,0.18)' }}>
                          <h4 className={styles.toolHeadTitle} style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Force transfer</h4>
                          <p className={styles.toolHeadSub} style={{ marginBottom: '0.75rem' }}>
                            Moves tokens from any holder&apos;s ATA to a destination wallet&apos;s ATA. Creates the destination ATA if it doesn&apos;t exist (paid by you).
                          </p>
                          <div className={styles.toolInputWrap} style={{ marginBottom: '0.5rem' }}>
                            <input
                              className={styles.toolInput}
                              type="text"
                              value={pdSourceOwner}
                              onChange={(e) => setPdSourceOwner(e.target.value)}
                              placeholder="Source holder wallet address"
                              spellCheck={false}
                              disabled={isLoading || !isPermanentDelegate}
                              aria-label="Source holder wallet"
                            />
                          </div>
                          <div className={styles.toolInputWrap} style={{ marginBottom: '0.5rem' }}>
                            <input
                              className={styles.toolInput}
                              type="text"
                              value={pdDestOwner}
                              onChange={(e) => setPdDestOwner(e.target.value)}
                              placeholder="Destination wallet address"
                              spellCheck={false}
                              disabled={isLoading || !isPermanentDelegate}
                              aria-label="Destination wallet"
                            />
                          </div>
                          <div className={styles.toolForm}>
                            <div className={styles.toolInputWrap}>
                              <input
                                className={styles.toolInput}
                                type="number"
                                value={pdTransferAmount}
                                onChange={(e) => setPdTransferAmount(e.target.value)}
                                placeholder="0.00"
                                step="0.000000001"
                                min="0"
                                disabled={isLoading || !isPermanentDelegate}
                                aria-label="Amount to transfer"
                              />
                              <span className={styles.toolInputSuffix}>tokens</span>
                            </div>
                            <button
                              className={`${styles.toolBtn} ${styles.warnBtn}`}
                              onClick={handleForceTransfer}
                              disabled={isLoading || !isPermanentDelegate || !pdSourceOwner.trim() || !pdDestOwner.trim() || !pdTransferAmount || !publicKey}
                            >
                              {isLoading ? <ClipLoader size={14} color="#0A0F1C" /> : <>Force transfer<ArrowIcon /></>}
                            </button>
                          </div>
                        </div>

                        {/* Force burn */}
                        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,179,71,0.18)' }}>
                          <h4 className={styles.toolHeadTitle} style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Force burn</h4>
                          <p className={styles.toolHeadSub} style={{ marginBottom: '0.75rem' }}>
                            Destroys tokens from any holder&apos;s ATA. Reduces total supply by the burned amount.
                          </p>
                          <div className={styles.toolInputWrap} style={{ marginBottom: '0.5rem' }}>
                            <input
                              className={styles.toolInput}
                              type="text"
                              value={pdBurnSourceOwner}
                              onChange={(e) => setPdBurnSourceOwner(e.target.value)}
                              placeholder="Source holder wallet address"
                              spellCheck={false}
                              disabled={isLoading || !isPermanentDelegate}
                              aria-label="Source holder wallet for burn"
                            />
                          </div>
                          <div className={styles.toolForm}>
                            <div className={styles.toolInputWrap}>
                              <input
                                className={styles.toolInput}
                                type="number"
                                value={pdBurnAmount}
                                onChange={(e) => setPdBurnAmount(e.target.value)}
                                placeholder="0.00"
                                step="0.000000001"
                                min="0"
                                disabled={isLoading || !isPermanentDelegate}
                                aria-label="Amount to burn"
                              />
                              <span className={styles.toolInputSuffix}>tokens</span>
                            </div>
                            <button
                              className={`${styles.toolBtn} ${styles.warnBtn}`}
                              onClick={handleForceBurn}
                              disabled={isLoading || !isPermanentDelegate || !pdBurnSourceOwner.trim() || !pdBurnAmount || !publicKey}
                            >
                              {isLoading ? <ClipLoader size={14} color="#0A0F1C" /> : <>Force burn<ArrowIcon /></>}
                            </button>
                          </div>
                        </div>

                        {tokenState.permanentDelegate.delegate && !isPermanentDelegate && (
                          <p className={styles.toolFootnote} style={{ marginTop: '0.75rem' }}>You are not the permanent delegate for this token.</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Auditor decrypt — Token-2022 confidential transfers only */}
            {FEATURES.ENABLE_CONFIDENTIAL_TRANSFER && tokenState.confidentialTransfer && (
              <section className={styles.section} aria-label="Decrypt confidential transfers">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle}>Decrypt confidential transfers</h2>
                  <span className={`${styles.sectionTagline} ${styles.infoTagline}`}>Auditor tool · client-side</span>
                </div>
                <div className={`${styles.toolCard} ${styles.infoVariant}`}>
                  <div className={styles.toolInner}>
                    <div className={styles.toolHead}>
                      <div>
                        <h3 className={styles.toolHeadTitle}>Recover cleartext amount</h3>
                        <p className={styles.toolHeadSub}>
                          If your wallet matches the auditor key on the mint, you can decrypt any qualifying transfer entirely in your browser. We never see your key, your signature, or the decrypted amount.
                        </p>
                      </div>
                      <span className={styles.toolBadge}>Audit</span>
                    </div>

                    <div className={styles.auditorMeta}>
                      <strong>Configured auditor:</strong>{' '}
                      {tokenState.confidentialTransfer.auditorElGamalPubkey
                        ? `${Buffer.from(tokenState.confidentialTransfer.auditorElGamalPubkey).toString('base64').slice(0, 12)}…${Buffer.from(tokenState.confidentialTransfer.auditorElGamalPubkey).toString('base64').slice(-8)} (base64, 32 bytes)`
                        : 'None — transfers on this mint cannot be decrypted by anyone other than sender or recipient.'}
                      <br />
                      <strong>Auto-approve new accounts:</strong>{' '}
                      {tokenState.confidentialTransfer.autoApproveNewAccounts ? 'Yes' : 'No'}
                      {' · '}
                      <strong>Config authority:</strong>{' '}
                      {tokenState.confidentialTransfer.authority
                        ? shortAddr(tokenState.confidentialTransfer.authority.toString(), 6, 6)
                        : 'Revoked'}
                    </div>

                    <p className={styles.toolFootnote} style={{ marginTop: '0.75rem' }}>
                      <strong>Scope.</strong> You can only decrypt transfers that included your ElGamal pubkey as the auditor at the time the transfer was sent. If the auditor was changed later, older transfers won&apos;t decrypt with the new key.
                    </p>

                    <div className={styles.auditorRow}>
                      <button
                        className={`${styles.toolBtn} ${styles.infoBtn}`}
                        onClick={handleDeriveAuditorKey}
                        disabled={auditorBusy || !publicKey || !signMessage || !tokenState.confidentialTransfer.auditorElGamalPubkey}
                      >
                        {auditorBusy && !auditorSecret
                          ? <ClipLoader size={14} color="#0A0F1C" />
                          : auditorSecret ? 'Re-derive auditor key' : 'Sign with wallet to derive auditor key'}
                      </button>

                      {!signMessage && publicKey && (
                        <p className={styles.toolFootnote}>
                          Your wallet doesn&apos;t expose <code>signMessage</code>. Use a Solana wallet that supports message signing — some hardware wallets may produce non-deterministic signatures and won&apos;t work here.
                        </p>
                      )}

                      {auditorPubkeyBytes && tokenState.confidentialTransfer.auditorElGamalPubkey && (
                        (() => {
                          const a = auditorPubkeyBytes;
                          const b = tokenState.confidentialTransfer.auditorElGamalPubkey;
                          let match = a.length === b.length;
                          if (match) {
                            for (let i = 0; i < a.length; i++) {
                              if (a[i] !== b[i]) { match = false; break; }
                            }
                          }
                          return (
                            <p className={`${styles.toolFootnote} ${match ? styles.ok : styles.err}`}>
                              Derived pubkey: <code>{Buffer.from(a).toString('base64').slice(0, 12)}…</code>
                              {' — '}
                              <strong>{match ? '✓ matches mint auditor' : '✗ does NOT match mint auditor'}</strong>
                            </p>
                          );
                        })()
                      )}

                      <div className={styles.toolInputWrap}>
                        <input
                          className={styles.toolInput}
                          type="text"
                          value={auditorTxSig}
                          onChange={(e) => setAuditorTxSig(e.target.value)}
                          placeholder="Paste a confidential-transfer transaction signature"
                          spellCheck={false}
                          disabled={auditorBusy || !auditorSecret}
                          aria-label="Transaction signature to decrypt"
                          style={{ paddingRight: '1rem' }}
                        />
                      </div>

                      <button
                        className={`${styles.toolBtn} ${styles.infoBtn}`}
                        onClick={handleAuditorDecrypt}
                        disabled={auditorBusy || !auditorSecret || !auditorTxSig.trim()}
                      >
                        {auditorBusy && !!auditorSecret
                          ? <ClipLoader size={14} color="#0A0F1C" />
                          : 'Decrypt amount'}
                      </button>

                      {auditorResult && auditorResult.ok && (
                        <div className={styles.auditorResult}>
                          <span className={styles.snapshotLabel}>Decrypted amount</span>
                          <p className={styles.bigNumber}>
                            {(Number(auditorResult.amount) / Math.pow(10, tokenState.decimals)).toLocaleString(undefined, { maximumFractionDigits: tokenState.decimals })}
                          </p>
                          <span className={styles.smallUnit}>
                            raw: {auditorResult.amount.toString()} · {tokenState.decimals} decimals
                          </span>
                        </div>
                      )}

                      {auditorResult && auditorResult.ok === false && (
                        <p className={`${styles.toolFootnote} ${styles.err}`}>
                          {(auditorResult as { error: string; ok: false }).error}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Primer */}
        <section className={styles.primer}>
          <div className={styles.primerHead}>
            <div className={styles.primerKicker}>Primer</div>
            <h2 className={styles.primerTitle}>Understanding Solana token authorities</h2>
            <p className={styles.primerLede}>
              A 60-second breakdown of the three permission fields baked into every SPL token, and what revoking each one actually means.
            </p>
          </div>

          <div className={styles.primerGrid}>
            <div className={styles.primerCard}>
              <div className={styles.primerNum}>01</div>
              <h3>Mint Authority</h3>
              <p>
                Controls who can create new tokens. Revoking permanently locks the token supply, preventing any additional tokens from being minted. Useful when you want to cap your token&apos;s total supply.
              </p>
            </div>
            <div className={styles.primerCard}>
              <div className={styles.primerNum}>02</div>
              <h3>Freeze Authority</h3>
              <p>
                Allows freezing token accounts, preventing transfers. Revoking ensures token holders can always transfer their tokens, increasing trust and decentralization.
              </p>
            </div>
            <div className={styles.primerCard}>
              <div className={styles.primerNum}>03</div>
              <h3>Update Authority</h3>
              <p>
                Controls who can modify token metadata (name, symbol, URI). Revoking permanently removes the ability to change metadata, ensuring your token&apos;s information cannot be altered.
              </p>
            </div>
          </div>
        </section>

        {/* Step-by-step flows. Mirrors MANAGE_TOKEN_HOWTOS, which the page
            head emits as HowTo markup. */}
        {MANAGE_TOKEN_HOWTOS.map((flow) => (
          <section key={flow.id} className={styles.primer}>
            <div className={styles.primerHead}>
              <div className={styles.primerKicker}>Step by step</div>
              <h2 className={styles.primerTitle}>{flow.name}</h2>
              <p className={styles.primerLede}>{flow.description}</p>
            </div>

            <div className={styles.primerGrid}>
              {flow.steps.map((step, i) => (
                <div key={step.name} className={styles.primerCard}>
                  <div className={styles.primerNum}>
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3>{step.name}</h3>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Ethereum note */}
        <section className={styles.etherNote}>
          <div className={styles.etherMark}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 12 8.5 17 11.5 12 2" />
              <polygon points="12 2 7 11.5 12 8.5 12 2" />
              <polygon points="12 15 12 22 17 13 12 15" />
              <polygon points="12 22 12 15 7 13 12 22" />
            </svg>
          </div>
          <div>
            <h3>Coming from Ethereum?</h3>
            <p>
              If you&apos;re searching for &quot;renounce ownership,&quot; the Solana equivalent is <strong>&quot;revoke authority.&quot;</strong> Ethereum renounces contract ownership; Solana revokes specific permissions (mint, freeze, or update) independently — giving you finer-grained control over exactly what you give up.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className={styles.faq}>
          <div className={`${styles.primerHead} ${styles.faqHead}`}>
            <div className={styles.primerKicker}>FAQ</div>
            <h2 className={styles.primerTitle}>Frequently Asked Questions</h2>
            <p className={styles.primerLede}>
              Fifteen of the most common questions about managing Solana tokens, renouncing authority, and minting supply.
            </p>
          </div>

          <div className={styles.faqList}>
            {faqs.map((faq, i) => {
              const open = expandedFaq === faq.id;
              return (
                <div key={faq.id} className={`${styles.faqItem} ${open ? styles.faqOpen : ''}`}>
                  <button
                    type="button"
                    className={styles.faqTrigger}
                    onClick={() => setExpandedFaq(open ? null : faq.id)}
                    aria-expanded={open}
                  >
                    <span className={styles.faqQ}>
                      <span className={styles.faqQIndex}>{String(i + 1).padStart(2, '0')}</span>
                      {faq.question}
                    </span>
                    <span className={styles.faqChevron}><ChevronIcon /></span>
                  </button>
                  {open && (
                    <div className={styles.faqBody}>
                      {typeof faq.answer === 'string' ? <p>{faq.answer}</p> : <p>{faq.answer}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Related */}
        <section className={styles.related}>
          <div className={styles.relatedHead}>Related resources</div>
          <ul className={styles.relatedList}>
            <li>
              <Link href="/create" className={styles.relatedLink}>
                <span>Create a new Solana token</span>
                <ArrowIcon />
              </Link>
            </li>
            <li>
              <Link href="/blog" className={styles.relatedLink}>
                <span>Learn more about Solana tokens</span>
                <ArrowIcon />
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};
